import React, { FC, useEffect } from 'react';
import { Text, View } from 'react-native';
import Animated, { useSharedValue, useAnimatedStyle, withRepeat, withTiming, cancelAnimation, Easing } from 'react-native-reanimated';
import i18n from 'i18n-js';
import { scale } from '@components/frame';
import { font } from '@styles/fonts';
import Separator from '@components/common/Separator';
import { ColorFunction } from '@styles/colors/types';
import BlockWrapper from '@v2/pages/machines/modules/now/components/Overview/components/BlockWrapper';
import { TourTarget } from '@components/tours';
import { WELCOME_TOUR_IDS } from '@components/tours/config/global/welcome/constants';
import { useManageMachineActivityBlock } from './hooks/useManageMachineActivityBlock';

import { injectConditionalObject, isPhone } from '@helpers';
import { role } from '@components/frame';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { isRestrictedForOperator, RESTRICTION_CATEGORIES } from '@config/operatorRestrictions';
import { InfoColumn } from '@v2/pages/machines/modules/now/components/Overview/components/InfoColumn';
import TimeDisplayInStaticFormat from '@components/common/TimeDisplayInStaticFormat';
import { ACTIVE_MACHINE_ICON_ANIMATION_CONFIG, END_TIME_TEXT_VARIANTS, GAP_BETWEEN_TIME_BLOCKS, SPINNING_ICON_CONFIG } from '@v2/pages/machines/modules/now/components/Overview/constants';
import Svg from '@styles/icons/svg';
import { ActiveMachineAnimatedIcon } from './components/ActiveMachineAnimatedIcon';

interface MachineActivityBlockProps {
  C: ColorFunction;
}

const MachineActivityBlock: FC<MachineActivityBlockProps> = ({ C }) => {
  const {
    machineStatus,
    statusColor,
    statusName,
    iconName,
    statusDescription,
    showExpectedEnd,
    progress,
    startedTime,
    expectedEnd,
    targetDuration,
    cycleTime,
    elapsedSeconds,
    handlePress,
    shouldRunElapsedSeconds,
    shouldDisplaySinceTime,
    sinceTime,
    batchLoaded,
  } = useManageMachineActivityBlock();

  const isReadOnly = isRestrictedForOperator(role, RESTRICTION_CATEGORIES.LIVE_OVERVIEW_READ_ONLY, 'MachineActivityBlock');
  const onPress = isReadOnly || !batchLoaded ? undefined : handlePress;

  const { spinningIconName, duration: spinDuration } = SPINNING_ICON_CONFIG;
  const { activeIconName, iconSize } = ACTIVE_MACHINE_ICON_ANIMATION_CONFIG;

  const isSpinning = iconName === spinningIconName;
  const isActive = iconName === activeIconName;

  const rotation = useSharedValue(0);

  useEffect(() => {
    if (isSpinning) {
      rotation.value = 0;
      rotation.value = withRepeat(withTiming(360, { duration: spinDuration, easing: Easing.linear }), -1, false);
    } else {
      cancelAnimation(rotation);
      rotation.value = 0;
    }

    return () => cancelAnimation(rotation);
  }, [isSpinning, spinDuration]);

  const spinningStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${rotation.value}deg` }],
  }));

  if (!machineStatus) return null;

  const iconConfig = { icon: iconName, color: statusColor, width: scale(iconSize), height: scale(iconSize) };

  const renderIcon = () => {
    if (isActive) {
      return <ActiveMachineAnimatedIcon C={C} color={statusColor} size={iconSize} />;
    }
    if (isSpinning) {
      return (
        <Animated.View style={spinningStyle}>
          <Svg {...iconConfig} />
        </Animated.View>
      );
    }
    return <Svg {...iconConfig} />;
  };

  if (isPhone) {
    return (
      <TourTarget tourId={WELCOME_TOUR_IDS.LIVE_ACTIVITY_BLOCK}>
        <BlockWrapper testID={TEST_IDS.liveScreen.activityBlock} onPress={onPress} containerStyle={{ paddingTop: scale(7), paddingBottom: scale(11) }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(12) }}>
            {renderIcon()}
            <View style={{ flex: 1 }}>
              <Text style={[font['Title 2'], { color: C(statusColor) }]}>{i18n.t(statusName)}</Text>
              <Text style={[font.Footnote, { color: C('Label/Light/Tertiary') }]}>{statusDescription}</Text>
              <ProgressRow
                C={C}
                progress={progress}
                statusColor={statusColor}
                elapsedSeconds={elapsedSeconds}
                targetDuration={targetDuration}
                shouldRunElapsedSeconds={shouldRunElapsedSeconds}
                shouldDisplaySinceTime={shouldDisplaySinceTime}
                sinceTime={sinceTime}
                batchLoaded={batchLoaded}
              />
            </View>
          </View>

          <View style={{ marginVertical: scale(16) }}>
            <Separator C={C} />
          </View>

          {/* Info row */}
          <View style={{ flexDirection: 'row' }}>
            <View style={{ flex: 1 }}>
              {cycleTime != null && <InfoColumn label={i18n.t('CYCLE')} C={C} targetDuration={cycleTime} isShortTime />}
            </View>

            <View style={{ flex: 1, alignItems: 'center' }}>
              <InfoColumn containerStyle={{ flex: 1 }} label={i18n.t('STARTED')} C={C} value={startedTime} valueColor={'Label/Light/Primary'} />
            </View>

            <View style={{ flex: 1 }}>
              <InfoColumn
                containerStyle={{ marginLeft: 'auto', ...injectConditionalObject(!showExpectedEnd, { opacity: 0 }) }}
                label={END_TIME_TEXT_VARIANTS.expectedEnd}
                C={C}
                value={expectedEnd}
                valueColor={'Label/Light/Primary'}
              />
            </View>
          </View>
        </BlockWrapper>
      </TourTarget>
    );
  }

  // Web / Tablet layout
  return (
    <TourTarget tourId={WELCOME_TOUR_IDS.LIVE_ACTIVITY_BLOCK}>
      <BlockWrapper testID={TEST_IDS.liveScreen.activityBlock} onPress={onPress} containerStyle={{ paddingVertical: scale(18) }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(20) }}>
          <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}>
            {renderIcon()}
            <View style={{ marginLeft: scale(15) }}>
              <Text style={[font['Title 2'], { color: C(statusColor) }]}>{i18n.t(statusName)}</Text>
              <Text style={[font.Footnote, { color: C('Label/Light/Tertiary') }]}>{statusDescription}</Text>
              <ProgressRow
                C={C}
                progress={progress}
                statusColor={statusColor}
                elapsedSeconds={elapsedSeconds}
                targetDuration={targetDuration}
                shouldRunElapsedSeconds={shouldRunElapsedSeconds}
                shouldDisplaySinceTime={shouldDisplaySinceTime}
                sinceTime={sinceTime}
                batchLoaded={batchLoaded}
              />
            </View>
          </View>

          <View style={{ flexDirection: 'row', alignSelf: 'flex-start', gap: scale(GAP_BETWEEN_TIME_BLOCKS), flexShrink: 0 }}>
            {cycleTime != null && <InfoColumn label={i18n.t('CYCLE')} C={C} targetDuration={cycleTime} isShortTime />}

            <InfoColumn label={i18n.t('STARTED')} C={C} value={startedTime} valueColor={'Label/Light/Primary'} />

            <InfoColumn
              containerStyle={{ ...injectConditionalObject(!showExpectedEnd, { opacity: 0 }) }}
              label={END_TIME_TEXT_VARIANTS.expectedEnd}
              C={C}
              value={expectedEnd}
              valueColor={'Label/Light/Primary'}
            />
          </View>
        </View>
      </BlockWrapper>
    </TourTarget>
  );
};

interface ProgressRowProps {
  C: ColorFunction;
  progress: number;
  statusColor: string;
  elapsedSeconds: number;
  targetDuration: number | null;
  shouldRunElapsedSeconds?: boolean;
  shouldDisplaySinceTime?: boolean;
  sinceTime?: string;
  batchLoaded: boolean;
}

const ProgressRow: FC<ProgressRowProps> = ({
  C,
  progress,
  statusColor,
  elapsedSeconds,
  targetDuration,
  shouldRunElapsedSeconds,
  shouldDisplaySinceTime,
  sinceTime,
  batchLoaded,
}) => {
  const targetDurationExist = targetDuration != null;
  const shouldDisplayTargetDuration = batchLoaded && targetDurationExist;

  const progressValue = useSharedValue(progress);
  const remainingSeconds = targetDuration ? Math.max(0, targetDuration - elapsedSeconds) : 0;
  const shouldAnimate = shouldRunElapsedSeconds && targetDurationExist && remainingSeconds > 0;

  useEffect(() => {
    if (shouldAnimate) {
      progressValue.value = progress;
      progressValue.value = withTiming(1, { duration: remainingSeconds * 1000, easing: Easing.linear });
    } else {
      cancelAnimation(progressValue);
      progressValue.value = progress;
    }

    return () => cancelAnimation(progressValue);
  }, [progress, remainingSeconds, shouldAnimate]);

  const animatedProgressStyle = useAnimatedStyle(() => ({
    flex: progressValue.value,
    height: '100%',
    borderRadius: scale(3),
  }));

  const animatedRemainingStyle = useAnimatedStyle(() => ({
    flex: 1 - progressValue.value,
  }));

  return (
    <View style={{ marginTop: scale(12) }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(10) }}>
        {targetDurationExist && (
          <View
            style={[
              {
                height: scale(6),
                borderRadius: scale(3),
                backgroundColor: C('Background/Light/Chart'),
                overflow: 'hidden',
                flexBasis: scale(110),
                flexGrow: 0,
                flexDirection: 'row',
              },
              isPhone && { flexShrink: 1 },
            ]}
          >
            <Animated.View
              style={[
                animatedProgressStyle,
                {
                  backgroundColor: C(statusColor),
                },
              ]}
            />
            <Animated.View style={animatedRemainingStyle} />
          </View>
        )}
        <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
          {!shouldDisplaySinceTime ? (
            <TimeDisplayInStaticFormat
              C={C}
              timeValue={elapsedSeconds}
              color={'Label/Light/Primary'}
              fontForDelimiter="Caption 1"
              fontForSecondDigit="Time 4"
              shouldRunTime={shouldRunElapsedSeconds}
            />
          ) : (
            <Text style={[{ color: C('Label/Light/Primary') }, font['Subhead 1']]} numberOfLines={1}>
              {sinceTime}
            </Text>
          )}
          {shouldDisplayTargetDuration && (
            <>
              <Text style={[font['Time 4'], { color: C('Label/Light/Tertiary') }]}> {i18n.t('of')} </Text>
              <TimeDisplayInStaticFormat
                C={C}
                timeValue={targetDuration}
                color={'Label/Light/Tertiary'}
                fontForDelimiter="Caption 1"
                fontForSecondDigit="Time 4"
              />
            </>
          )}
        </View>
      </View>
    </View>
  );
};

export { MachineActivityBlock };
